import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import { Redirect, Route, Switch, useLocation } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import { UpdateBanner } from "./components/UpdateBanner";

import Home from "./pages/Home";
import Login from "./pages/Login";
import Landing from "./pages/Landing";
import Inbox from "./pages/Inbox";
import InboxReports from "./pages/InboxReports";
import Contacts from "./pages/Contacts";
import Tags from "./pages/Tags";
import Settings from "./pages/Settings";
import ConectarIA from "./pages/ConectarIA";
import ConectarMeta from "./pages/ConectarMeta";
import LinkedAccounts from "./pages/LinkedAccounts";
import ContactDetail from "./pages/ContactDetail";
import Templates from "./pages/Templates";
import TemplateEditor from "./pages/TemplateEditor";
import Automations from "./pages/Automations";
import Messages from "./pages/Messages";
import OptIn from "./pages/OptIn";
import Flows from "./pages/Flows";
import FlowBuilder from "./pages/FlowBuilder";
import FlowExecutionsPage from "./pages/FlowExecutionsPage";
import WhatsAppFlows from "./pages/WhatsAppFlows";
import WhatsAppFlowBuilder from "./pages/WhatsAppFlowBuilder";
import WhatsAppDashboard from "./pages/WhatsAppDashboard";
import WhatsAppReports from "./pages/WhatsAppReports";
import Sequences from "./pages/Sequences";
import SequenceDetail from "./pages/SequenceDetail";
import Segments from "./pages/Segments";
import UserManagement from "./pages/UserManagement";
import RoleManagement from "./pages/RoleManagement";
import EmailTemplates from "./pages/EmailTemplates";
import EmailDashboard from "./pages/EmailDashboard";
import EmailHygiene from "./pages/EmailHygiene";
import EmailCreateRedirect from "./pages/EmailCreateRedirect";
import EmailCampaignReport from "./pages/EmailCampaignReport";
import EmailSendPage from "./pages/EmailSendPage";
import Emails from "./pages/Emails";
import Ads from "./pages/Ads";
import Cobranca from "./pages/Cobranca";
import AdsCampaign from "./pages/AdsCampaign";
import AdsBoost from "./pages/AdsBoost";
import AdsAttribution from "./pages/AdsAttribution";
import InstagramComments from "./pages/InstagramComments";
import SocialCalendar from "./pages/SocialCalendar";
import SocialInsights from "./pages/SocialInsights";
import SocialContas from "./pages/SocialContas";
import SocialCaptacao from "./pages/SocialCaptacao";
import InstagramAutomations from "./pages/InstagramAutomations";
import InstagramAutomationEditor from "./pages/InstagramAutomationEditor";
import Copiloto from "./pages/Copiloto";
import Assistente from "./pages/Assistente";
import { AssistenteDockGate } from "./features/assistente/AssistenteDock";
import CopilotoInsights from "./pages/CopilotoInsights";
import IntegrationStatus from "./pages/IntegrationStatus";
import AutoAssignmentSettings from "./pages/AutoAssignmentSettings";
import Privacidade from "./pages/Privacidade";
import Termos from "./pages/Termos";
import Lgpd from "./pages/Lgpd";
import ExclusaoDeDados from "./pages/ExclusaoDeDados";
import Onboarding from "./pages/Onboarding";
import ContactHierarchy from "./pages/ContactHierarchy";
import Broadcast from "./pages/Broadcast";
import BroadcastStepper from "./pages/BroadcastStepper";
import CampaignReport from "./pages/CampaignReport";
import Admin from "./pages/Admin";
import SuperAdmin from "./pages/SuperAdmin";
import LeadScoring from "./pages/LeadScoring";
import WhatsAppTest from "./pages/WhatsAppTest";
import MessageTracerDashboard from "./pages/MessageTracerDashboard";
import AIInsights from "./pages/AIInsights";
import Intelligence from "./pages/Intelligence";
import CrmDeals from "./pages/CrmDeals";
import Agenda from "./pages/Agenda";
import CrmDealDetail from "./pages/CrmDealDetail";
import CrmTasks from "./pages/CrmTasks";
import CrmReports from "./pages/CrmReports";
import Prospecting from "./pages/Prospecting";
import CrmProducts from "./pages/CrmProducts";
import CrmProposals from "./pages/CrmProposals";
import CrmProductDetail from "./pages/CrmProductDetail";
import CrmProductCompare from "./pages/CrmProductCompare";
import CrmSellers from "./pages/CrmSellers";
import CrmSellerDetail from "./pages/CrmSellerDetail";
import CrmSellerCompare from "./pages/CrmSellerCompare";
import CrmCommissions from "./pages/CrmCommissions";
import CrmOrganizations from "./pages/CrmOrganizations";
import CrmOrganizationDetail from "./pages/CrmOrganizationDetail";
import DevelopmentProgress from "./pages/DevelopmentProgress";
// F1.1 — IA (BYOK + Agentes)
import LLMCredentials from "./pages/LLMCredentials";
import Agents from "./pages/Agents";
import AgentEditor from "./pages/agents/AgentEditor";
// F1.4 — Métricas Routing Engine
import RoutingMetrics from "./pages/RoutingMetrics";

function Router() {
  return (
    <Switch>
        <Route path={"/login"} component={Login} />
        <Route path={"/landing"} component={Landing} />
        <Route path={"/privacidade"} component={Privacidade} />
        <Route path={"/termos"} component={Termos} />
        <Route path={"/lgpd"} component={Lgpd} />
        <Route path={"/exclusao-de-dados"} component={ExclusaoDeDados} />
        <Route path={"/onboarding"} component={Onboarding} />
        <Route path={"/super-admin"} component={SuperAdmin} />
        <Route path={"/"} component={Home} />
        <Route path={"/whatsapp/reports"} component={WhatsAppReports} />
        <Route path={"/whatsapp"} component={WhatsAppDashboard} />
        <Route path={"/dashboard"}>{() => <Redirect to="/whatsapp" />}</Route>
        <Route path={"/contacts"} component={Contacts} />
        <Route path={"/contacts/hierarchy"} component={ContactHierarchy} />
        <Route path={"/templates/new"} component={TemplateEditor} />
        <Route path={"/templates/:id/edit"} component={TemplateEditor} />
        <Route path={"/templates"} component={Templates} />
        <Route path={"/broadcast/create"} component={BroadcastStepper} />
        <Route path="/broadcast/:id/report" component={CampaignReport} />
        <Route path={"/broadcast/:id"} component={BroadcastStepper} />
        <Route path={"/broadcast"} component={Broadcast} />
        <Route path={"/automations"} component={Automations} />
        <Route path={"/settings"} component={Settings} />
        <Route path={"/conectar-ia"} component={ConectarIA} />
        <Route path={"/conectar-meta"} component={ConectarMeta} />
        <Route path={"/contas-vinculadas"} component={LinkedAccounts} />
        <Route path={"/tags"} component={Tags} />
        <Route path={"/contacts/:id"} component={ContactDetail} />
        <Route path="/reports">{() => <Redirect to="/whatsapp/reports" />}</Route>
        <Route path="/messages" component={Messages} />
        <Route path="/optin" component={OptIn} />
        <Route path="/flows/create" component={FlowBuilder} />
        <Route path="/flows/:id/executions" component={FlowExecutionsPage} />
        <Route path="/flows/:id" component={FlowBuilder} />
        <Route path="/flows" component={Flows} />
        <Route path="/whatsapp-flows" component={WhatsAppFlows} />
        <Route path="/whatsapp-flows/:id/executions" component={FlowExecutionsPage} />
        <Route path="/whatsapp-flows/:id" component={WhatsAppFlowBuilder} />
        <Route path="/inbox/reports" component={InboxReports} />
        <Route path="/inbox" component={Inbox} />
        <Route path="/sequences" component={Sequences} />
        <Route path="/sequences/:id" component={SequenceDetail} />
        <Route path="/segments" component={Segments} />
        <Route path="/users" component={UserManagement} />
        <Route path="/roles" component={RoleManagement} />

        <Route path="/intelligence" component={Intelligence} />
        {/* F1.1 — IA (BYOK + Agentes) */}
        <Route path="/llm-credentials" component={LLMCredentials} />
        {/* Páginas dedicadas: /agents/new (criar) e /agents/:id (editar)
            precisam vir ANTES de /agents (lista) na ordem do wouter Switch. */}
        <Route path="/agents/new" component={AgentEditor} />
        <Route path="/agents/:id" component={AgentEditor} />
        <Route path="/agents" component={Agents} />
        {/* F1.4 — Métricas Routing Engine */}
        <Route path="/routing-metrics" component={RoutingMetrics} />
        <Route path="/emails/products">{() => <Redirect to="/intelligence?tab=products" />}</Route>
        <Route path="/emails/audiences">{() => <Redirect to="/intelligence?tab=audiences" />}</Route>
        <Route path="/emails/brand-voice">{() => <Redirect to="/intelligence?tab=brand-voice" />}</Route>
        <Route path="/emails/templates/:id/edit" component={Emails} />
        <Route path="/emails/higiene" component={EmailHygiene} />
        <Route path="/emails/templates" component={EmailTemplates} />
        <Route path="/emails/create" component={EmailCreateRedirect} />
        <Route path="/emails/campaigns/:id" component={EmailCampaignReport} />
        <Route path="/emails/:id/send" component={EmailSendPage} />
        <Route path="/emails/:id/editor" component={Emails} />
        {/* Rota legada: campanhas tinham /emails/:id (stepper). Hoje vai direto pro editor. */}
        <Route path="/emails/:id">
          {(params) => <Redirect to={`/emails/${params.id}/editor`} />}
        </Route>
        <Route path="/cobranca" component={Cobranca} />
        <Route path="/ads/atribuicao" component={AdsAttribution} />
        <Route path="/ads/new" component={AdsBoost} />
        <Route path="/ads/campaigns/:id" component={AdsCampaign} />
        <Route path="/ads" component={Ads} />
        <Route path="/instagram/comments" component={InstagramComments} />
        <Route path="/social/insights" component={SocialInsights} />
        <Route path="/social/contas" component={SocialContas} />
        <Route path="/social/captacao" component={SocialCaptacao} />
        <Route path="/social" component={SocialCalendar} />
        <Route path="/instagram/automations" component={InstagramAutomations} />
        <Route path="/instagram/automations/:id" component={InstagramAutomationEditor} />
        <Route path="/assistente" component={Assistente} />
        <Route path="/copiloto" component={Copiloto} />
        <Route path="/copiloto/insights" component={CopilotoInsights} />
        <Route path="/emails" component={EmailDashboard} />
        <Route path="/lead-scoring" component={LeadScoring} />
        <Route path="/whatsapp-test" component={WhatsAppTest} />
        <Route path="/whatsapp-status" component={IntegrationStatus} />
        <Route path="/message-tracer" component={MessageTracerDashboard} />
        <Route path="/admin" component={Admin} />
        <Route path="/auto-assignment" component={AutoAssignmentSettings} />
        <Route path="/knowledge-base">{() => <Redirect to="/intelligence?tab=documents" />}</Route>
        <Route path="/ai-insights" component={AIInsights} />
        <Route path="/agenda" component={Agenda} />
        <Route path="/crm/prospecting" component={Prospecting} />
        <Route path="/crm/deals/:id" component={CrmDealDetail} />
        <Route path="/crm/deals" component={CrmDeals} />
        <Route path="/crm/tasks" component={CrmTasks} />
        <Route path="/crm/proposals" component={CrmProposals} />
        <Route path="/crm/reports" component={CrmReports} />
        <Route path="/crm/products/compare" component={CrmProductCompare} />
        <Route path="/crm/products/:id" component={CrmProductDetail} />
        <Route path="/crm/products" component={CrmProducts} />
        <Route path="/crm/sellers/compare" component={CrmSellerCompare} />
        <Route path="/crm/sellers/:userId" component={CrmSellerDetail} />
        <Route path="/crm/sellers" component={CrmSellers} />
        <Route path="/crm/commissions" component={CrmCommissions} />
        <Route path="/crm/organizations/:slug" component={CrmOrganizationDetail} />
        <Route path="/crm/organizations" component={CrmOrganizations} />
        <Route path="/crm" component={CrmDeals} />
        <Route path="/progress" component={DevelopmentProgress} />
        <Route path={"/404"} component={NotFound} />
        <Route component={NotFound} />
      </Switch>
  );
}

function App() {
  const [location] = useLocation();
  return (
    <ErrorBoundary resetKey={location}>
      <ThemeProvider defaultTheme="light">
        <TooltipProvider>
          <Toaster position="bottom-right" />
          <UpdateBanner />
          <Router />
          {/* Fora do Router de propósito: cada página monta o próprio
              DashboardLayout, e um dock dentro dele morreria (com o turno em
              andamento) a cada troca de rota. Aqui ele sobrevive à navegação. */}
          <AssistenteDockGate />
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
